import React, { useState, useEffect } from 'react';
import { Container, Card, Badge, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Eye, Search, Filter } from 'lucide-react';
import { emailTemplatesService } from '../services/emailService';
import './Email.css';

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, categoryFilter]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailTemplatesService.getAllTemplates({ isActive: true });
      
      if (response.status && response.data) {
        setTemplates(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    setFilteredTemplates(filtered);
  };

  const categories = ['all', ...new Set(templates.map(t => t.category))];

  const handlePreview = async (template) => {
    try {
      setPreviewLoading(true);
      setSelectedTemplate(template);
      // Preview functionality can be enhanced later
    } catch (err) {
      console.error('Error previewing template:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Container fluid className="email-page">
      {/* Header */}
      <div className="email-header">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/email')}
              className="d-flex align-items-center gap-2"
            >
              <ArrowLeft size={18} />
              Back
            </Button>
            <div>
              <h2 className="email-page-title mb-0">Email Templates</h2>
              <p className="text-muted mb-0">Browse and manage email templates</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>Search Templates</Form.Label>
              <div className="position-relative">
                <Search size={18} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  type="text"
                  placeholder="Search by name, code, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-5"
                />
              </div>
            </div>
            <div className="col-md-6">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Templates List */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner />
          <p className="mt-3 text-muted">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <FileText size={48} className="text-muted mb-3" />
            <p className="text-muted">
              {searchTerm || categoryFilter !== 'all' 
                ? 'No templates found matching your filters.' 
                : 'No email templates available.'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div className="templates-grid">
          {filteredTemplates.map(template => (
            <Card key={template.id} className="template-card">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-1">{template.name}</h5>
                    <Badge bg="secondary" className="me-2">
                      {template.category}
                    </Badge>
                    {template.isSystem && (
                      <Badge bg="info">System</Badge>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {template.description && (
                  <p className="text-muted small mb-2">{template.description}</p>
                )}
                <div className="template-info">
                  <small className="text-muted d-block mb-1">
                    <strong>Code:</strong> {template.code}
                  </small>
                  {template.requiredEntity && (
                    <small className="text-muted d-block mb-1">
                      <strong>Requires:</strong> {template.requiredEntityLabel || template.requiredEntity}
                    </small>
                  )}
                  {template.optionalEntities && template.optionalEntities.length > 0 && (
                    <small className="text-muted d-block">
                      <strong>Optional:</strong> {template.optionalEntities.join(', ')}
                    </small>
                  )}
                </div>
                <div className="mt-3">
                  <small className="text-muted">
                    <strong>Subject:</strong> {template.subject.substring(0, 60)}
                    {template.subject.length > 60 ? '...' : ''}
                  </small>
                </div>
              </Card.Body>
              <Card.Footer className="bg-transparent">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    navigate('/email', { state: { selectedTemplate: template.code } });
                  }}
                >
                  Use Template
                </Button>
              </Card.Footer>
            </Card>
          ))}
        </div>
      )}
    </Container>
  );
};

export default EmailTemplates;

